import "../../css/WorkspaceSettings.css";
import { useTheme } from "../../contexts/ThemeContext";
import "../../css/DocumentationHomeComponent.css";
import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill-new";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../firebase.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import { templatesApi } from "../../utils/templatesApi";
import TemplateEditorModal from "../shared/templates/TemplateEditorModal";
import "../shared/templates/TemplateGallery.css";

const API = "https://bluebirddocumentationadmin.pythonanywhere.com";

function WorkspaceSettings() {

    const [userInfo, setUserInfo]         = useState(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [workspace, setWorkspace]       = useState(null);
    const [loading, setLoading]           = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Authenticating user...");
    const [error, setError]               = useState(null);

    // Role
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [ownerHasThemeAccess, setOwnerHasThemeAccess] = useState(false);

    // Active sidebar section
    const [activeSection, setActiveSection] = useState("profile");

    // ── Workspace fields (ADMIN/OWNER) ──────────────────────────
    const [workspaceChanges, setWorkspaceChanges] = useState({
        name: "", description: "", custom_domain_title: "", custom_domain: "", theme: "Default"
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedTheme, setSelectedTheme]         = useState("Default");

    const [isEditingName, setIsEditingName]               = useState(false);
    const [editedName, setEditedName]                     = useState("");
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription]       = useState("");
    const [isEditingCustomDomain, setIsEditingCustomDomain] = useState(false);
    const [editedCustomDomain, setEditedCustomDomain]     = useState("");
    const [formattedCustomDomain, setFormattedCustomDomain] = useState("");

    // ── Team members ─────────────────────────────────────────────
    const [teamMembers, setTeamMembers]           = useState([]);
    const [teamMembersLoaded, setTeamMembersLoaded] = useState(false);
    const [currentEmail, setCurrentEmail]         = useState("");
    const [newMemberRole, setNewMemberRole]       = useState("EDITOR");
    const [isCheckingEmail, setIsCheckingEmail]   = useState(false);
    const [emailExists, setEmailExists]           = useState(null);
    const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);

    // ── Linked boards ─────────────────────────────────────────────
    const [linkedBoards, setLinkedBoards]         = useState([]);
    const [linkableBoards, setLinkableBoards]     = useState([]);
    const [selectedBoardToLink, setSelectedBoardToLink] = useState("");
    const [isLinkingBoard, setIsLinkingBoard]     = useState(false);

    // ── Dashboard prefs ───────────────────────────────────────────
    const [dashboardPreferences, setDashboardPreferences] = useState({
        pinnedDocuments: true, myFavorites: true, mostViewedDocuments: true,
        mostActiveSpaces: true, recentlyViewed: true, staleDocuments: true, snapshots: true
    });
    const [widgetOrder, setWidgetOrder] = useState([
        "pinnedDocuments", "myFavorites", "mostViewedDocuments",
        "mostActiveSpaces", "recentlyViewed", "staleDocuments", "snapshots"
    ]);

    // ── My Profile (visible to all roles) ────────────────────────
    const [myAboutMe, setMyAboutMe]     = useState("");
    const [myPosition, setMyPosition]   = useState("");
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [profileSaved, setProfileSaved] = useState(false);

    // ── Onboarding config (ADMIN/OWNER) ──────────────────────────
    const [onboarding, setOnboarding] = useState(null);         // null = not yet fetched
    const [onboardingLoaded, setOnboardingLoaded] = useState(false);
    const [isSavingOnboarding, setIsSavingOnboarding] = useState(false);
    const [onboardingSaved, setOnboardingSaved] = useState(false);
    const [obEnabled, setObEnabled]             = useState(true);
    const [obWelcome, setObWelcome]             = useState("");
    const [obWalkthroughId, setObWalkthroughId] = useState("");
    const [obQuickLinks, setObQuickLinks]       = useState([]);   // [{id,label,url}]
    const [obChecklist, setObChecklist]         = useState([]);   // [{id,label}]
    const [obPocId, setObPocId]                 = useState("");
    const [obWalkthroughs, setObWalkthroughs]   = useState([]);
    const [newLinkLabel, setNewLinkLabel]       = useState("");
    const [newLinkUrl, setNewLinkUrl]           = useState("");
    const [newCheckItem, setNewCheckItem]       = useState("");

    // ── Workspace API Keys ────────────────────────────────────────
    const [wsApiKeyPolicy, setWsApiKeyPolicy]   = useState(null);
    const [wsApiKeys, setWsApiKeys]             = useState([]);
    const [wsApiKeysLoaded, setWsApiKeysLoaded] = useState(false);
    const [wsApiKeysError, setWsApiKeysError]   = useState('');
    const [wsNewKeyName, setWsNewKeyName]       = useState('');
    const [wsNewKeyScopes, setWsNewKeyScopes]   = useState({ read: true, write: false, delete: false });
    const [wsNewKeyRPH, setWsNewKeyRPH]         = useState(100);
    const [wsGeneratedKey, setWsGeneratedKey]   = useState(null);
    const [wsApiKeysLoading, setWsApiKeysLoading] = useState(false);
    const [wsIsSavingPolicy, setWsIsSavingPolicy] = useState(false);
    const [wsPolicySaved, setWsPolicySaved]     = useState(false);

    const { customDomain } = useParams();
    const navigate         = useNavigate();
    const { enhancedRightClickEnabled } = useQuickLinks();

    // ── Headers helper ────────────────────────────────────────────
    const authHeaders = async () => {
        const token = await userInfo.getIdToken();
        return {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            "Auth-Token": `Bearer ${token}`,
            "Requesting-User-Uid": userInfo.uid,
        };
    };

    // ── Theme (BBD-81): preview + apply via ThemeProvider override ─
    const { setWorkspaceTheme } = useTheme();

    // ── Auth ──────────────────────────────────────────────────────
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(user => {
            setUserInfo(user || null);
            setAuthResolved(true);
        });
        return () => unsub();
    }, []);

    // ── Derive role ───────────────────────────────────────────────
    useEffect(() => {
        if (!userInfo || teamMembers.length === 0) return;
        const me = teamMembers.find(m => m.email === userInfo.email);
        if (me) setCurrentUserRole(me.role);
        else if (workspace?.owner?.email === userInfo.email) setCurrentUserRole("OWNER");
    }, [teamMembers, userInfo, workspace]);

    // ── Loading gate ──────────────────────────────────────────────
    useEffect(() => {
        if (workspace && teamMembersLoaded && currentUserRole !== null) setLoading(false);
    }, [workspace, teamMembersLoaded, currentUserRole]);

    // ── Populate form when workspace loads ────────────────────────
    useEffect(() => {
        if (!workspace) return;
        const themeToUse = !ownerHasThemeAccess && workspace.theme !== "Default"
            ? "Default" : (workspace.theme || "Default");
        setWorkspaceChanges({
            name: workspace.name,
            description: workspace.description || "",
            custom_domain_title: workspace.custom_domain_title || workspace.name,
            custom_domain: workspace.custom_domain || "",
            theme: themeToUse,
        });
        setWorkspaceTheme(themeToUse);
        setSelectedTheme(themeToUse);
    }, [workspace, ownerHasThemeAccess, setWorkspaceTheme]);

    // ── Fetch workspace ───────────────────────────────────────────
    useEffect(() => {
        if (!authResolved) return;
        if (!userInfo) { setError("You must be logged in to view this page"); setLoading(false); return; }
        const ctrl = new AbortController();
        (async () => {
            setLoadingMessage("Fetching workspace details...");
            try {
                const token = await userInfo.getIdToken();
                const res = await fetch(`${API}/workspaces/domain/${customDomain}/`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${token}`,
                        "Auth-Token": `Bearer ${token}`,
                        "Requesting-User-Uid": userInfo.uid,
                    },
                    signal: ctrl.signal,
                });
                if (!res.ok) throw new Error(`${res.status}`);
                const data = await res.json();
                setWorkspace(data);
                setEditedName(data.name);
                setEditedCustomDomain(data.custom_domain_title);
                setFormattedCustomDomain(data.custom_domain);
                // Workspace themes follow the owner's theme access: any paid storage
                // plan or VIP (BBD-99). Falls back to is_subscriber until deployed.
                setOwnerHasThemeAccess(typeof data.owner?.has_theme_access === "boolean"
                    ? data.owner.has_theme_access
                    : Boolean(data.owner?.is_subscriber));
            } catch (e) {
                if (e.name !== "AbortError") { setError("Error fetching workspace details"); setLoading(false); }
            }
        })();
        return () => ctrl.abort();
    }, [customDomain, authResolved, userInfo]);

    // ── Fetch secondary data when workspace ready ─────────────────
    useEffect(() => {
        if (!workspace?.id) return;
        fetchTeamMembers();
        fetchDashboardPreferences();
        fetchLinkedBoards();
        fetchOnboarding();
    }, [workspace]);

    // ── Templates section (BBD-110) ───────────────────────────────
    const [wsTemplates, setWsTemplates] = useState([]);
    const [wsPresets, setWsPresets] = useState([]);
    const [wsDefaultTemplateRef, setWsDefaultTemplateRef] = useState("");
    const [wsTemplatesLoaded, setWsTemplatesLoaded] = useState(false);
    const [wsTemplatesError, setWsTemplatesError] = useState("");
    const [templateEditor, setTemplateEditor] = useState({ open: false, template: null });
    const [templateBusyId, setTemplateBusyId] = useState(null);

    const fetchWsTemplates = async () => {
        if (!workspace?.id) return;
        try {
            setWsTemplatesError("");
            const [list, gallery, presets] = await Promise.all([
                templatesApi.list({ workspaceId: workspace.id, includeContent: false }),
                templatesApi.gallery({ workspaceId: workspace.id, includeContent: false }),
                templatesApi.listPresets({ includeContent: false }),
            ]);
            setWsTemplates(list.templates || []);
            setWsDefaultTemplateRef(gallery.default_template_ref || "");
            setWsPresets((presets.presets || []).filter(p => p.kind !== "snippet"));
            setWsTemplatesLoaded(true);
        } catch (e) {
            setWsTemplatesError(e.message || "Could not load templates");
        }
    };

    useEffect(() => {
        if (activeSection !== 'templates' || !workspace?.id || wsTemplatesLoaded) return;
        fetchWsTemplates();
    }, [activeSection, workspace]);

    const withTemplateBusy = async (id, fn) => {
        setTemplateBusyId(id);
        setWsTemplatesError("");
        try { await fn(); } catch (e) { setWsTemplatesError(e.message || "Something went wrong"); } finally { setTemplateBusyId(null); }
    };

    const saveDefaultTemplateRef = async (ref) => {
        try {
            setWsTemplatesError("");
            const res = await fetch(`${API}/workspaces/${workspace.id}/update/`, {
                method: "PUT",
                headers: await authHeaders(),
                body: JSON.stringify({ default_template_ref: ref || null }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Could not save the default template");
            setWsDefaultTemplateRef(data.default_template_ref || "");
        } catch (e) {
            setWsTemplatesError(e.message);
        }
    };

    // ── Lazy-load API keys when section is first opened ───────────
    useEffect(() => {
        if (activeSection !== 'apikeys' || !workspace?.id || wsApiKeysLoaded) return;
        fetchWsApiKeyPolicy();
        fetchWsApiKeys();
    }, [activeSection, workspace]);

    // ── Fetch my profile when team members loaded ─────────────────
    useEffect(() => {
        if (!teamMembersLoaded || !userInfo || teamMembers.length === 0) return;
        const me = teamMembers.find(m => m.email === userInfo.email);
        if (me) {
            setMyAboutMe(me.about_me || "");
            setMyPosition(me.position || "");
        }
    }, [teamMembersLoaded, teamMembers, userInfo]);

    // ─────────────────────────────────────────────────────────────
    // Data fetchers
    // ─────────────────────────────────────────────────────────────
    const fetchTeamMembers = async () => {
        setLoadingMessage("Fetching team members...");
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/members/`, { headers });
            if (!res.ok) throw new Error(`${res.status}`);
            const data = await res.json();
            setTeamMembers(data.map(m => ({
                email: m.email, role: m.role, name: m.name,
                userId: m.user_id, joinedAt: m.joined_at,
                invitedBy: m.invited_by, about_me: m.about_me, position: m.position,
            })));
        } catch (e) {
            console.error("Error fetching team members:", e);
        } finally {
            setTeamMembersLoaded(true);
        }
    };

    const fetchDashboardPreferences = async () => {
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/dashboard-preferences/`, { headers });
            if (!res.ok) return;
            const data = await res.json();
            if (data.preferences) setDashboardPreferences(data.preferences);
            if (data.widget_order) setWidgetOrder(data.widget_order);
        } catch (e) { console.error(e); }
    };

    const fetchLinkedBoards = async () => {
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/linked-boards/`, { headers });
            if (res.ok) setLinkedBoards(await res.json());
        } catch (e) { console.error(e); }
    };

    const fetchLinkableBoards = async () => {
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/linked-boards/linkable/`, { headers });
            if (res.ok) setLinkableBoards(await res.json());
        } catch (e) { console.error(e); }
    };

    // ─────────────────────────────────────────────────────────────
    // My Profile
    // ─────────────────────────────────────────────────────────────
    const saveMyProfile = async () => {
        setIsSavingProfile(true);
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/members/my-profile/update/`, {
                method: "PATCH", headers, body: JSON.stringify({ about_me: myAboutMe, position: myPosition }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            setProfileSaved(true);
            setTimeout(() => setProfileSaved(false), 3000);
        } catch (e) {
            console.error("Error saving profile:", e);
            alert("Failed to save profile. Please try again.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Onboarding
    // ─────────────────────────────────────────────────────────────
    const fetchOnboarding = async () => {
        if (!workspace?.id) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/onboarding/`, { headers });
            if (!res.ok) return;
            const data = await res.json();
            setOnboarding(data);
            if (data.exists) {
                setObEnabled(data.is_enabled);
                setObWelcome(data.welcome_message || "");
                setObWalkthroughId(data.walkthrough ? String(data.walkthrough.id) : "");
                setObQuickLinks(data.quick_links || []);
                setObChecklist(data.checklist_items || []);
                setObPocId(data.point_of_contact ? String(data.point_of_contact.user_id) : "");
            }
        } catch (e) { console.error(e); }
        finally { setOnboardingLoaded(true); }
    };

    const fetchObWalkthroughs = async () => {
        if (!workspace?.id || obWalkthroughs.length > 0) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/walkthroughs/`, { headers });
            if (!res.ok) return;
            const data = await res.json();
            setObWalkthroughs(data);
        } catch (e) { console.error(e); }
    };

    const saveOnboarding = async () => {
        setIsSavingOnboarding(true);
        try {
            const headers = await authHeaders();
            const body = {
                is_enabled: obEnabled,
                welcome_message: obWelcome,
                walkthrough_id: obWalkthroughId ? parseInt(obWalkthroughId) : null,
                quick_links: obQuickLinks,
                checklist_items: obChecklist,
                point_of_contact_id: obPocId ? parseInt(obPocId) : null,
            };
            const res = await fetch(`${API}/workspaces/${workspace.id}/onboarding/update/`, {
                method: "PATCH", headers, body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            setOnboardingSaved(true);
            setTimeout(() => setOnboardingSaved(false), 3000);
            await fetchOnboarding();
        } catch (e) {
            console.error("Error saving onboarding:", e);
            alert("Failed to save onboarding settings. Please try again.");
        } finally { setIsSavingOnboarding(false); }
    };

    const addQuickLink = () => {
        if (!newLinkLabel.trim() || !newLinkUrl.trim()) return;
        setObQuickLinks(prev => [...prev, { id: Date.now().toString(), label: newLinkLabel.trim(), url: newLinkUrl.trim() }]);
        setNewLinkLabel(""); setNewLinkUrl("");
    };

    const removeQuickLink = (id) => setObQuickLinks(prev => prev.filter(l => l.id !== id));

    const addCheckItem = () => {
        if (!newCheckItem.trim()) return;
        setObChecklist(prev => [...prev, { id: Date.now().toString(), label: newCheckItem.trim() }]);
        setNewCheckItem("");
    };

    const removeCheckItem = (id) => setObChecklist(prev => prev.filter(c => c.id !== id));

    // ─────────────────────────────────────────────────────────────
    // Workspace API Key helpers
    // ─────────────────────────────────────────────────────────────

    const fetchWsApiKeyPolicy = async () => {
        if (!workspace?.id) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/api-key-policy/`, { headers });
            if (res.ok) {
                const data = await res.json();
                setWsApiKeyPolicy(data);
                if (data.your_policy) {
                    const allowed = data.your_policy.allowed_scopes || ['read'];
                    setWsNewKeyScopes({
                        read: allowed.includes('read'),
                        write: allowed.includes('write'),
                        delete: allowed.includes('delete'),
                    });
                    setWsNewKeyRPH(Math.min(wsNewKeyRPH, data.your_policy.max_rph || 100));
                }
            }
        } catch (e) { console.error(e); }
    };

    const fetchWsApiKeys = async () => {
        if (!workspace?.id) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/api-keys/`, { headers });
            if (res.ok) {
                const data = await res.json();
                setWsApiKeys(data.api_keys || []);
            }
        } catch (e) { console.error(e); }
        finally { setWsApiKeysLoaded(true); }
    };

    const createWsApiKey = async () => {
        if (!wsNewKeyName.trim()) { setWsApiKeysError('Key name is required.'); return; }
        const scopes = Object.entries(wsNewKeyScopes).filter(([, v]) => v).map(([k]) => k);
        if (scopes.length === 0) { setWsApiKeysError('Select at least one permission scope.'); return; }
        setWsApiKeysLoading(true);
        setWsApiKeysError('');
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/api-keys/create/`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ name: wsNewKeyName.trim(), scopes, requests_per_hour: wsNewKeyRPH }),
            });
            const data = await res.json();
            if (res.ok) {
                setWsGeneratedKey(data.key);
                setWsNewKeyName('');
                fetchWsApiKeys();
            } else {
                setWsApiKeysError(data.error || 'Failed to create API key.');
            }
        } catch (e) { setWsApiKeysError('Failed to create API key.'); }
        finally { setWsApiKeysLoading(false); }
    };

    const revokeWsApiKey = async (keyId, keyName) => {
        if (!window.confirm(`Revoke the key "${keyName}"? This cannot be undone.`)) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/api-keys/${keyId}/revoke/`, {
                method: 'DELETE', headers,
            });
            if (res.ok) fetchWsApiKeys();
            else setWsApiKeysError('Failed to revoke key.');
        } catch (e) { setWsApiKeysError('Failed to revoke key.'); }
    };

    const disableWsApiKey = async (keyId, keyName) => {
        if (!window.confirm(`Disable the key "${keyName}"? The member will not be able to use this key.`)) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/api-keys/${keyId}/disable/`, {
                method: 'PATCH', headers,
            });
            if (res.ok) fetchWsApiKeys();
            else setWsApiKeysError('Failed to disable key.');
        } catch (e) { setWsApiKeysError('Failed to disable key.'); }
    };

    const saveWsApiKeyPolicy = async (updatedPolicy) => {
        setWsIsSavingPolicy(true);
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/api-key-policy/update/`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(updatedPolicy),
            });
            if (res.ok) {
                const data = await res.json();
                setWsApiKeyPolicy(prev => ({ ...prev, ...data }));
                setWsPolicySaved(true);
                setTimeout(() => setWsPolicySaved(false), 3000);
            } else {
                setWsApiKeysError('Failed to save policy.');
            }
        } catch (e) { setWsApiKeysError('Failed to save policy.'); }
        finally { setWsIsSavingPolicy(false); }
    };

    // ─────────────────────────────────────────────────────────────
    // Workspace name / description / domain
    // ─────────────────────────────────────────────────────────────
    const formatCustomDomain = (text) => {
        if (!text) return "";
        return text.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-+|-+$/g, "");
    };

    const saveWorkspaceName = async () => {
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/update/`, {
                method: "PATCH", headers, body: JSON.stringify({ name: editedName }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            setWorkspace({ ...workspace, name: editedName });
            setIsEditingName(false);
        } catch (e) { console.error(e); }
    };

    const saveWorkspaceDescription = async () => {
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/update/`, {
                method: "PATCH", headers, body: JSON.stringify({ description: editedDescription }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            setWorkspace({ ...workspace, description: editedDescription });
            setIsEditingDescription(false);
        } catch (e) { console.error(e); }
    };

    const saveWorkspaceCustomDomain = async () => {
        try {
            const newFormatted = formatCustomDomain(editedCustomDomain);
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/update/`, {
                method: "PATCH", headers,
                body: JSON.stringify({ custom_domain_title: editedCustomDomain, custom_domain: newFormatted }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            setWorkspace({ ...workspace, custom_domain_title: editedCustomDomain, custom_domain: newFormatted });
            setFormattedCustomDomain(newFormatted);
            setIsEditingCustomDomain(false);
            navigate(`/workspace/${newFormatted}/settings`);
        } catch (e) { console.error(e); }
    };

    // ─────────────────────────────────────────────────────────────
    // Save all (ADMIN/OWNER bulk)
    // ─────────────────────────────────────────────────────────────
    const saveAllChanges = async () => {
        try {
            const headers = await authHeaders();

            await fetch(`${API}/workspaces/${workspace.id}/update/`, {
                method: "PATCH", headers, body: JSON.stringify(workspaceChanges),
            });

            // Members sync
            const membersRes = await fetch(`${API}/workspaces/${workspace.id}/members/`, { headers });
            const serverMembers = membersRes.ok ? await membersRes.json() : [];
            const formatted = serverMembers.map(m => ({ ...m, userId: m.user_id }));

            const updatePs = teamMembers
                .filter(m => m.userId)
                .map(m => {
                    const sv = formatted.find(s => s.user_id === m.userId);
                    if (sv && sv.role !== m.role) {
                        return fetch(`${API}/workspaces/${workspace.id}/members/${m.userId}/update/`, {
                            method: "PATCH", headers, body: JSON.stringify({ role: m.role }),
                        });
                    }
                    return Promise.resolve();
                });

            // BBD-73: an add can now be refused because of a block, so keep the
            // email alongside the request — allSettled would otherwise swallow it
            // and the save would report success for a member who was never added.
            const pendingAdds = teamMembers.filter(m => !m.userId);
            const addPs = pendingAdds
                .map(m => fetch(`${API}/workspaces/${workspace.id}/members/add/`, {
                    method: "POST", headers, body: JSON.stringify({ email: m.email, role: m.role }),
                }));

            const removePs = formatted
                .filter(sv => sv.role !== "OWNER" && !teamMembers.some(lm => lm.userId === sv.user_id))
                .map(sv => fetch(`${API}/workspaces/${workspace.id}/members/${sv.user_id}/remove/`, {
                    method: "DELETE", headers,
                }));

            const [updateResults, addResults, removeResults] = await Promise.all([
                Promise.allSettled(updatePs),
                Promise.allSettled(addPs),
                Promise.allSettled(removePs),
            ]);
            void updateResults;
            void removeResults;

            const failedAdds = [];
            for (let i = 0; i < addResults.length; i += 1) {
                const result = addResults[i];
                if (result.status !== "fulfilled" || !result.value.ok) {
                    let reason = "could not be added";
                    if (result.status === "fulfilled") {
                        const body = await result.value.json().catch(() => ({}));
                        reason = body.error || reason;
                    }
                    failedAdds.push(`${pendingAdds[i].email} — ${reason}`);
                }
            }

            await fetchTeamMembers();

            // Dashboard prefs
            await fetch(`${API}/workspaces/${workspace.id}/dashboard-preferences/update/`, {
                method: "PATCH", headers,
                body: JSON.stringify({ preferences: dashboardPreferences, widget_order: widgetOrder }),
            });

            setWorkspace({ ...workspace, ...workspaceChanges });
            setIsEditingName(false);
            setIsEditingDescription(false);
            setIsEditingCustomDomain(false);
            setHasUnsavedChanges(false);
            if (failedAdds.length > 0) {
                alert(`Settings saved, but some members were not added:\n\n${failedAdds.join("\n")}`);
            } else {
                alert("Settings saved.");
            }
        } catch (e) {
            console.error(e);
            alert("Failed to save settings. Please try again.");
        }
    };

    const discardChanges = () => {
        setWorkspaceChanges({
            name: workspace.name, description: workspace.description || "",
            custom_domain_title: workspace.custom_domain_title || workspace.name,
            custom_domain: workspace.custom_domain || "", theme: workspace.theme || "Default",
        });
        setEditedName(workspace.name);
        setEditedDescription(workspace.description || "");
        setEditedCustomDomain(workspace.custom_domain_title || workspace.name);
        setSelectedTheme(workspace.theme || "Default");
        setIsEditingName(false);
        setIsEditingDescription(false);
        setIsEditingCustomDomain(false);
        setHasUnsavedChanges(false);
        fetchTeamMembers();
    };

    // ─────────────────────────────────────────────────────────────
    // Members
    // ─────────────────────────────────────────────────────────────
    const checkEmailExists = async (email) => {
        if (!email.includes("@")) { setEmailExists(null); return; }
        setIsCheckingEmail(true);
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/users/check-email-exists/`, {
                method: "POST", headers, body: JSON.stringify({ email }),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setEmailExists(data.exists);
        } catch { setEmailExists(null); }
        finally { setIsCheckingEmail(false); }
    };

    const handleEmailInputChange = (e) => {
        const email = e.target.value;
        setCurrentEmail(email);
        if (emailCheckTimeout) clearTimeout(emailCheckTimeout);
        if (!email) { setEmailExists(null); return; }
        setEmailCheckTimeout(setTimeout(() => checkEmailExists(email), 500));
    };

    const handleAddTeamMember = () => {
        if (!currentEmail.includes("@")) { alert("Please enter a valid email address."); return; }
        if (teamMembers.some(m => m.email === currentEmail)) { alert("This email has already been added."); return; }
        setTeamMembers([...teamMembers, { email: currentEmail, role: newMemberRole, isExistingUser: emailExists }]);
        setCurrentEmail("");
        setEmailExists(null);
        setHasUnsavedChanges(true);
    };

    const handleRemoveTeamMember = (index) => {
        if (teamMembers[index].role === "OWNER") { alert("You cannot remove the workspace owner."); return; }
        const updated = [...teamMembers];
        updated.splice(index, 1);
        setTeamMembers(updated);
        setHasUnsavedChanges(true);
    };

    const handleRoleChange = (index, newRole) => {
        const updated = [...teamMembers];
        updated[index].role = newRole;
        setTeamMembers(updated);
        setHasUnsavedChanges(true);
    };

    // ─────────────────────────────────────────────────────────────
    // Dashboard widgets
    // ─────────────────────────────────────────────────────────────
    const handleDashboardPreferenceChange = (section) => {
        setDashboardPreferences(p => ({ ...p, [section]: !p[section] }));
        setHasUnsavedChanges(true);
    };

    const handleMoveWidget = (index, dir) => {
        const order = [...widgetOrder];
        if (order[index] === "snapshots") return;
        if (dir === "up" && index > 0 && order[index - 1] !== "snapshots") {
            [order[index], order[index - 1]] = [order[index - 1], order[index]];
        } else if (dir === "down" && index < order.length - 1 && order[index + 1] !== "snapshots") {
            [order[index], order[index + 1]] = [order[index + 1], order[index]];
        }
        setWidgetOrder(order);
        setHasUnsavedChanges(true);
    };

    const isWidgetMoveDisabled = (index, dir) => {
        if (widgetOrder[index] === "snapshots") return true;
        if (dir === "up") return index === 0 || widgetOrder[index - 1] === "snapshots";
        return index === widgetOrder.length - 1 || widgetOrder[index + 1] === "snapshots";
    };

    const getWidgetLabel = (widget) => ({
        pinnedDocuments: "Pinned Documents",
        myFavorites: "My Favorites",
        mostViewedDocuments: "Most Viewed Documents",
        mostActiveSpaces: "Most Active Spaces",
        recentlyViewed: "Recently Viewed",
        staleDocuments: "Stale Documents",
        snapshots: "Recent Workspace Snapshots",
    }[widget] || widget);

    // ─────────────────────────────────────────────────────────────
    // Linked boards
    // ─────────────────────────────────────────────────────────────
    const handleLinkBoard = async () => {
        if (!selectedBoardToLink) return;
        setIsLinkingBoard(true);
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/linked-boards/link/`, {
                method: "POST", headers, body: JSON.stringify({ board_id: selectedBoardToLink }),
            });
            if (res.ok) { setSelectedBoardToLink(""); fetchLinkedBoards(); fetchLinkableBoards(); }
            else { const d = await res.json(); alert(d.error || "Failed to link board"); }
        } catch (e) { console.error(e); }
        finally { setIsLinkingBoard(false); }
    };

    const handleUnlinkBoard = async (boardId) => {
        if (!window.confirm("Unlink this board? Existing board members will not be removed.")) return;
        try {
            const headers = await authHeaders();
            const res = await fetch(`${API}/workspaces/${workspace.id}/linked-boards/${boardId}/unlink/`, {
                method: "DELETE", headers,
            });
            if (res.ok) { fetchLinkedBoards(); fetchLinkableBoards(); }
            else { const d = await res.json(); alert(d.error || "Failed to unlink board"); }
        } catch (e) { console.error(e); }
    };

    // ─────────────────────────────────────────────────────────────
    // Delete workspace
    // ─────────────────────────────────────────────────────────────
    const handleDeleteWorkspace = async () => {
        if (!window.confirm("Are you sure you want to DELETE this workspace? This cannot be undone.")) return;
        const headers = await authHeaders();
        const res = await fetch(`${API}/workspaces/${workspace.id}/delete/`, { method: "DELETE", headers });
        if (!res.ok) { alert("Failed to delete workspace."); return; }
        navigate("/");
    };

    // ─────────────────────────────────────────────────────────────
    // Context menu
    // ─────────────────────────────────────────────────────────────
    const menuOptions = userInfo ? [
        { label: "Save Changes", icon: <i className="fas fa-save"></i>, onClick: saveAllChanges },
        { label: "Home", icon: <i className="fas fa-home"></i>, onClick: () => navigate("/") },
        { label: "Your Documents", onClick: () => navigate("/documentation") },
        { label: "Your Walkthroughs", onClick: () => navigate("/walkthroughs") },
        { label: "Your Workspace", onClick: () => navigate("/workspaces") },
        { label: "Account Settings", onClick: () => navigate(`/account/${userInfo.uid}`) },
    ] : [
        { label: "Login", onClick: () => navigate("/login") },
        { label: "Register", onClick: () => navigate("/register") },
    ];
    const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    // ─────────────────────────────────────────────────────────────
    // Loading / error states
    // ─────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="DocumentationHomeComponent__coffee-loader-div">
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div><div className="cup-handle"></div>
                        <div className="cup-base"></div><div className="cup-shadow"></div>
                        <div className="steam steam1"></div><div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: "#5B92E5", fontSize: "1.1rem", marginTop: 16 }}>{loadingMessage}</p>
            </div>
        );
    }

    if (error || (authResolved && !userInfo)) {
        return (
            <div style={{ padding: "60px", textAlign: "center" }}>
                <p>{error || "Please log in to view this page."}</p>
                <button onClick={() => navigate(error ? "/" : "/login")} style={{ marginTop: 16 }}>
                    {error ? "Home" : "Log In"}
                </button>
            </div>
        );
    }

    const isAdminOrOwner = currentUserRole === "OWNER" || currentUserRole === "ADMIN";

    // ─────────────────────────────────────────────────────────────
    // Nav items definition
    // ─────────────────────────────────────────────────────────────
    const navSections = [
        { id: "profile",    label: "My Profile",    icon: "fas fa-user-circle",   always: true },
        { id: "dashboard",  label: "Dashboard",     icon: "fas fa-th-large",      always: true },
        { id: "apikeys",    label: "API Keys",      icon: "fas fa-key",           always: true },
        { id: "templates",  label: "Templates",     icon: "fas fa-puzzle-piece",  always: true },
        { id: "general",    label: "General",       icon: "fas fa-sliders-h",     adminOnly: true },
        { id: "domain",     label: "Custom Domain", icon: "fas fa-link",          adminOnly: true },
        { id: "members",    label: "Members",       icon: "fas fa-users",         adminOnly: true },
        { id: "boards",     label: "Linked Boards", icon: "fas fa-columns",       adminOnly: true },
        { id: "theme",      label: "Theme",         icon: "fas fa-palette",       adminOnly: true, requiresThemeAccess: true },
        { id: "onboarding", label: "Onboarding",    icon: "fas fa-rocket",        adminOnly: true },
        { id: "danger",     label: "Danger Zone",   icon: "fas fa-exclamation-triangle", ownerOnly: true },
    ];

    const visibleSections = navSections.filter(s => {
        if (s.always) return true;
        if (s.ownerOnly) return currentUserRole === "OWNER";
        if (s.adminOnly) return isAdminOrOwner;
        return true;
    }).filter(s => {
        if (s.requiresThemeAccess) return ownerHasThemeAccess;
        return true;
    });

    // ─────────────────────────────────────────────────────────────
    // Section renderers
    // ─────────────────────────────────────────────────────────────

    const renderProfile = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">My Profile</h2>
                <p className="WorkspaceSettings__section-desc">
                    Visible to other members in this workspace.
                </p>
            </div>

            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Position / Role</label>
                <input
                    className="WorkspaceSettings__input"
                    type="text"
                    placeholder="e.g. Frontend Engineer, Product Manager..."
                    value={myPosition}
                    onChange={e => setMyPosition(e.target.value)}
                    maxLength={100}
                />
            </div>

            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">About Me</label>
                <textarea
                    className="WorkspaceSettings__textarea"
                    placeholder="A short bio or note for your teammates..."
                    value={myAboutMe}
                    onChange={e => setMyAboutMe(e.target.value)}
                />
            </div>

            <div className="WorkspaceSettings__btn-row">
                <button
                    className="WorkspaceSettings__btn WorkspaceSettings__btn--primary"
                    onClick={saveMyProfile}
                    disabled={isSavingProfile}
                >
                    {isSavingProfile
                        ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                        : <><i className="fas fa-check"></i> Save Profile</>
                    }
                </button>
                {profileSaved && (
                    <span style={{ fontSize: "0.8rem", color: "#16a34a", alignSelf: "center" }}>
                        <i className="fas fa-check-circle"></i> Saved
                    </span>
                )}
            </div>
        </>
    );

    const renderDashboard = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">Dashboard Preferences</h2>
                <p className="WorkspaceSettings__section-desc">
                    Choose which widgets appear on your workspace dashboard and arrange their order.
                </p>
            </div>

            {widgetOrder.map((widget, index) => (
                <div key={widget} className="WorkspaceSettings__widget-row">
                    <input
                        type="checkbox"
                        className="WorkspaceSettings__widget-checkbox"
                        checked={dashboardPreferences[widget]}
                        onChange={() => handleDashboardPreferenceChange(widget)}
                    />
                    <span className="WorkspaceSettings__widget-label">{getWidgetLabel(widget)}</span>
                    <button
                        className="WorkspaceSettings__widget-move-btn"
                        onClick={() => handleMoveWidget(index, "up")}
                        disabled={isWidgetMoveDisabled(index, "up")}
                        title="Move up"
                    ><i className="fas fa-chevron-up"></i></button>
                    <button
                        className="WorkspaceSettings__widget-move-btn"
                        onClick={() => handleMoveWidget(index, "down")}
                        disabled={isWidgetMoveDisabled(index, "down")}
                        title="Move down"
                    ><i className="fas fa-chevron-down"></i></button>
                </div>
            ))}

            <div className="WorkspaceSettings__btn-row" style={{ marginTop: 24 }}>
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={saveAllChanges}>
                    <i className="fas fa-save"></i> Save Preferences
                </button>
            </div>
        </>
    );

    const renderGeneral = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">General</h2>
                <p className="WorkspaceSettings__section-desc">
                    Workspace name and description. Click any field to edit.
                </p>
            </div>

            {/* Quick actions */}
            <div className="WorkspaceSettings__quick-actions" style={{ marginBottom: 28 }}>
                <button className="WorkspaceSettings__action-button" onClick={() => navigate(`/workspace/${workspace.custom_domain}/import/document`)} title="Import document">
                    <i className="fa-solid fa-file-import"></i>
                    <span className="WorkspaceSettings__action-button-text">Import Document</span>
                </button>
                <button className="WorkspaceSettings__action-button" onClick={() => navigate(`/workspace/${workspace.custom_domain}/import/space`)} title="Import space">
                    <i className="fa-solid fa-layer-group"></i>
                    <span className="WorkspaceSettings__action-button-text">Import Space</span>
                </button>
                <button className="WorkspaceSettings__action-button" onClick={() => navigate(`/workspace/${workspace.custom_domain}/pull-from-writer`)} title="Pull this workspace's notes from a local Bluebird Writer folder — every change needs your approval">
                    <i className="fa-solid fa-feather"></i>
                    <span className="WorkspaceSettings__action-button-text">Pull from Writer</span>
                </button>
            </div>

            {/* Workspace name */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Workspace Name</label>
                {isEditingName ? (
                    <div className="WorkspaceSettings__input-row">
                        <input
                            className="WorkspaceSettings__input"
                            type="text"
                            value={editedName}
                            onChange={e => { setEditedName(e.target.value); setWorkspaceChanges(c => ({ ...c, name: e.target.value })); setHasUnsavedChanges(true); }}
                            onKeyDown={e => { if (e.key === "Enter") saveWorkspaceName(); if (e.key === "Escape") { setIsEditingName(false); setEditedName(workspace.name); } }}
                            autoFocus
                        />
                        <div className="WorkspaceSettings__input-actions">
                            <button className="WorkspaceSettings__icon-btn WorkspaceSettings__icon-btn--confirm" onClick={saveWorkspaceName}><i className="fas fa-check"></i></button>
                            <button className="WorkspaceSettings__icon-btn WorkspaceSettings__icon-btn--cancel" onClick={() => { setIsEditingName(false); setEditedName(workspace.name); }}><i className="fas fa-times"></i></button>
                        </div>
                    </div>
                ) : (
                    <div className="WorkspaceSettings__field-value" onClick={() => setIsEditingName(true)}>
                        {workspace.name}
                        <i className="fas fa-pen WorkspaceSettings__edit-pen"></i>
                    </div>
                )}
            </div>

            {/* Description */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Description</label>
                {isEditingDescription ? (
                    <>
                        <textarea
                            className="WorkspaceSettings__textarea"
                            value={editedDescription}
                            onChange={e => { setEditedDescription(e.target.value); setWorkspaceChanges(c => ({ ...c, description: e.target.value })); setHasUnsavedChanges(true); }}
                            autoFocus
                        />
                        <div className="WorkspaceSettings__input-actions" style={{ marginTop: 6 }}>
                            <button className="WorkspaceSettings__icon-btn WorkspaceSettings__icon-btn--confirm" onClick={saveWorkspaceDescription}><i className="fas fa-check"></i></button>
                            <button className="WorkspaceSettings__icon-btn WorkspaceSettings__icon-btn--cancel" onClick={() => { setIsEditingDescription(false); setEditedDescription(workspace.description || ""); }}><i className="fas fa-times"></i></button>
                        </div>
                    </>
                ) : (
                    <div className="WorkspaceSettings__field-value" onClick={() => { setIsEditingDescription(true); setEditedDescription(workspace.description || ""); }}>
                        {workspace.description || <span style={{ opacity: 0.4 }}>No description</span>}
                        <i className="fas fa-pen WorkspaceSettings__edit-pen"></i>
                    </div>
                )}
            </div>

            {/* Owner */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Owner</label>
                <div className="WorkspaceSettings__field-value WorkspaceSettings__field-value--readonly">
                    {workspace.owner.email}
                </div>
            </div>

            <div className="WorkspaceSettings__btn-row">
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={saveAllChanges}>
                    <i className="fas fa-save"></i> Save
                </button>
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--ghost" onClick={discardChanges}>
                    Discard
                </button>
            </div>
        </>
    );

    const renderDomain = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">Custom Domain</h2>
                <p className="WorkspaceSettings__section-desc">
                    Your workspace URL slug. Must be unique across all workspaces.
                </p>
            </div>

            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Domain Name</label>
                {isEditingCustomDomain ? (
                    <>
                        <div className="WorkspaceSettings__input-row">
                            <input
                                className="WorkspaceSettings__input"
                                type="text"
                                value={editedCustomDomain}
                                onChange={e => {
                                    setEditedCustomDomain(e.target.value);
                                    const f = formatCustomDomain(e.target.value);
                                    setWorkspaceChanges(c => ({ ...c, custom_domain_title: e.target.value, custom_domain: f }));
                                    setHasUnsavedChanges(true);
                                }}
                                onKeyDown={e => { if (e.key === "Enter") saveWorkspaceCustomDomain(); if (e.key === "Escape") { setIsEditingCustomDomain(false); setEditedCustomDomain(workspace.custom_domain_title || workspace.name); } }}
                                autoFocus
                            />
                            <div className="WorkspaceSettings__input-actions">
                                <button className="WorkspaceSettings__icon-btn WorkspaceSettings__icon-btn--confirm" onClick={saveWorkspaceCustomDomain}><i className="fas fa-check"></i></button>
                                <button className="WorkspaceSettings__icon-btn WorkspaceSettings__icon-btn--cancel" onClick={() => { setIsEditingCustomDomain(false); setEditedCustomDomain(workspace.custom_domain_title || workspace.name); }}><i className="fas fa-times"></i></button>
                            </div>
                        </div>
                        <p className="WorkspaceSettings__field-hint">
                            Preview: bluebird-documentation.com/workspace/{formatCustomDomain(editedCustomDomain)}
                        </p>
                    </>
                ) : (
                    <>
                        <div className="WorkspaceSettings__field-value" onClick={() => { setIsEditingCustomDomain(true); setEditedCustomDomain(workspace.custom_domain_title || workspace.name); }}>
                            {workspace.custom_domain_title || workspace.name}
                            <i className="fas fa-pen WorkspaceSettings__edit-pen"></i>
                        </div>
                        <p className="WorkspaceSettings__field-hint">
                            bluebird-documentation.com/workspace/{formattedCustomDomain}
                        </p>
                    </>
                )}
            </div>
        </>
    );

    const renderMembers = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">Team Members</h2>
                <p className="WorkspaceSettings__section-desc">
                    Manage who has access to this workspace and their roles.{" "}
                    <a href="https://bluebird-documentation.com/documentation/page/Workspace%20Permissions/NGzFPNeV9GY1g6q2PFX7SfQmRqw1" target="_blank" rel="noopener noreferrer" style={{ color: "#5B92E5" }}>
                        Learn about roles
                    </a>
                </p>
            </div>

            <div className="WorkspaceSettings__info-note">
                <i className="fas fa-info-circle"></i>
                If an email is not yet registered, that person will need to create an account before they can access this workspace.
            </div>

            {teamMembers.map((member, index) => {
                const initials = (member.name || member.email).slice(0, 2).toUpperCase();
                return (
                    <div key={index} className="WorkspaceSettings__member-row">
                        <div className="WorkspaceSettings__member-avatar">{initials}</div>
                        <div className="WorkspaceSettings__member-info">
                            <div className="WorkspaceSettings__member-email">
                                {member.email}
                                {member.isExistingUser === false && (
                                    <span className="WorkspaceSettings__badge WorkspaceSettings__badge--new-user" style={{ marginLeft: 8 }}>
                                        Not a user
                                    </span>
                                )}
                            </div>
                            {member.position && (
                                <div className="WorkspaceSettings__member-meta">{member.position}</div>
                            )}
                        </div>
                        <select
                            className="WorkspaceSettings__role-select"
                            value={member.role}
                            onChange={e => handleRoleChange(index, e.target.value)}
                            disabled={member.role === "OWNER"}
                        >
                            <option value="ADMIN">Administrator</option>
                            <option value="EDITOR">Editor</option>
                            <option value="VIEWER">Viewer</option>
                            {member.role === "OWNER" && <option value="OWNER">Owner</option>}
                        </select>
                        <button
                            className="WorkspaceSettings__member-remove-btn"
                            onClick={() => handleRemoveTeamMember(index)}
                            disabled={member.role === "OWNER"}
                            title="Remove member"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                );
            })}

            <div className="WorkspaceSettings__add-member-row">
                <div className="WorkspaceSettings__add-member-input-wrap">
                    <input
                        className="WorkspaceSettings__input"
                        type="email"
                        placeholder="Enter email address..."
                        value={currentEmail}
                        onChange={handleEmailInputChange}
                        onKeyDown={e => e.key === "Enter" && handleAddTeamMember()}
                        style={{
                            borderColor: emailExists === true ? "#16a34a" : emailExists === false ? "#dc2626" : undefined,
                        }}
                    />
                    {isCheckingEmail && (
                        <i className="fas fa-spinner fa-spin WorkspaceSettings__status-icon" style={{ color: "#9ca3af" }}></i>
                    )}
                    {!isCheckingEmail && emailExists === true && (
                        <i className="fas fa-check-circle WorkspaceSettings__status-icon" style={{ color: "#16a34a" }}></i>
                    )}
                    {!isCheckingEmail && emailExists === false && (
                        <i className="fas fa-exclamation-circle WorkspaceSettings__status-icon" style={{ color: "#dc2626" }}></i>
                    )}
                </div>
                <select className="WorkspaceSettings__role-select" value={newMemberRole} onChange={e => setNewMemberRole(e.target.value)}>
                    <option value="ADMIN">Administrator</option>
                    <option value="EDITOR">Editor</option>
                    <option value="VIEWER">Viewer</option>
                </select>
                <button
                    className="WorkspaceSettings__btn WorkspaceSettings__btn--primary"
                    onClick={handleAddTeamMember}
                    disabled={isCheckingEmail}
                >
                    <i className="fas fa-plus"></i>
                </button>
            </div>

            <div className="WorkspaceSettings__btn-row" style={{ marginTop: 24 }}>
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={saveAllChanges}>
                    <i className="fas fa-save"></i> Save Changes
                </button>
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--ghost" onClick={discardChanges}>
                    Discard
                </button>
            </div>
        </>
    );

    const renderBoards = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">Linked Task Boards</h2>
                <p className="WorkspaceSettings__section-desc">
                    Link Bluebird Task Management boards. Members are automatically synced with matching roles.
                </p>
            </div>

            {linkedBoards.length === 0 ? (
                <p className="WorkspaceSettings__empty">No boards linked yet.</p>
            ) : linkedBoards.map(link => (
                <div key={link.id} className="WorkspaceSettings__board-row">
                    <i className="fas fa-columns" style={{ color: "#5B92E5", fontSize: "0.85rem" }}></i>
                    <span className="WorkspaceSettings__board-abbr">{link.board_abbreviation}</span>
                    <span>{link.board_name}</span>
                    <span className="WorkspaceSettings__board-owner">owned by {link.board_owner_email}</span>
                    <button
                        className="WorkspaceSettings__member-remove-btn"
                        onClick={() => handleUnlinkBoard(link.board_id)}
                        title="Unlink"
                    >
                        <i className="fas fa-unlink" style={{ color: "#ef4444" }}></i>
                    </button>
                </div>
            ))}

            <div className="WorkspaceSettings__add-member-row" style={{ marginTop: 12 }}>
                <select
                    className="WorkspaceSettings__input"
                    value={selectedBoardToLink}
                    onChange={e => setSelectedBoardToLink(e.target.value)}
                    onFocus={() => { if (linkableBoards.length === 0) fetchLinkableBoards(); }}
                >
                    <option value="">Select a board to link...</option>
                    {linkableBoards.map(b => (
                        <option key={b.id} value={b.id}>{b.abbreviation} — {b.name}</option>
                    ))}
                </select>
                <button
                    className="WorkspaceSettings__btn WorkspaceSettings__btn--primary"
                    onClick={handleLinkBoard}
                    disabled={!selectedBoardToLink || isLinkingBoard}
                >
                    {isLinkingBoard ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-link"></i>}
                </button>
            </div>
        </>
    );

    const renderTheme = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">Theme</h2>
                <p className="WorkspaceSettings__section-desc">
                    Customize the visual appearance of this workspace. Available to subscribers.
                </p>
            </div>

            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Workspace Theme</label>
                <select
                    className="WorkspaceSettings__theme-select"
                    value={selectedTheme}
                    onChange={e => {
                        setSelectedTheme(e.target.value);
                        setWorkspaceChanges(c => ({ ...c, theme: e.target.value }));
                        setWorkspaceTheme(e.target.value);
                        setHasUnsavedChanges(true);
                    }}
                >
                    <option value="Default">Default</option>
                    <option value="Dark Theme">Dark Theme</option>
                    <option value="Purple Theme">Purple Theme</option>
                </select>
            </div>

            <div className="WorkspaceSettings__btn-row">
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={saveAllChanges}>
                    <i className="fas fa-save"></i> Save
                </button>
            </div>
        </>
    );

    const renderDanger = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title" style={{ color: "#ef4444" }}>Danger Zone</h2>
                <p className="WorkspaceSettings__section-desc">
                    Irreversible actions. Proceed with caution.
                </p>
            </div>

            <div className="WorkspaceSettings__danger-zone">
                <p className="WorkspaceSettings__danger-zone-title">Delete Workspace</p>
                <p className="WorkspaceSettings__danger-zone-desc">
                    Permanently deletes this workspace and all its spaces, pages, and walkthroughs. This cannot be undone.
                </p>
                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--danger" onClick={handleDeleteWorkspace}>
                    <i className="fas fa-trash-alt"></i> Delete this workspace
                </button>
            </div>
        </>
    );

    const obQuillModules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
        ],
    };

    const renderOnboarding = () => (
        <>
            <div className="WorkspaceSettings__section-header">
                <h2 className="WorkspaceSettings__section-title">Onboarding</h2>
                <p className="WorkspaceSettings__section-desc">
                    Configure what new workspace members see when they first join.
                    All fields are optional — leave any section blank to omit it.
                </p>
            </div>

            {/* Enable toggle */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Onboarding Modal</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem" }}>
                        <input
                            type="checkbox"
                            checked={obEnabled}
                            onChange={e => setObEnabled(e.target.checked)}
                            style={{ width: 17, height: 17, accentColor: "#5B92E5", cursor: "pointer" }}
                        />
                        Show onboarding modal to new members
                    </label>
                </div>
                <p className="WorkspaceSettings__field-hint">
                    When enabled, new members see a welcome modal on their first visit. They can dismiss it or skip.
                </p>
            </div>

            <hr className="WorkspaceSettings__divider" />

            {/* Welcome message */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Welcome Message <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <div className="WorkspaceSettings__quill-wrapper">
                    <ReactQuill
                        theme="snow"
                        value={obWelcome}
                        onChange={setObWelcome}
                        modules={obQuillModules}
                        placeholder="Write a welcome message for new team members..."
                    />
                </div>
            </div>

            {/* Documentation path */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Documentation Path <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <select
                    className="WorkspaceSettings__theme-select"
                    value={obWalkthroughId}
                    onChange={e => setObWalkthroughId(e.target.value)}
                    onFocus={fetchObWalkthroughs}
                >
                    <option value="">None — no recommended walkthrough</option>
                    {obWalkthroughs.map(wt => (
                        <option key={wt.id} value={String(wt.id)}>{wt.walkthrough_name}</option>
                    ))}
                </select>
                <p className="WorkspaceSettings__field-hint">
                    A walkthrough that new members are guided to read first.
                </p>
            </div>

            {/* Quick links */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Quick Links <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                {obQuickLinks.map(link => (
                    <div key={link.id} className="WorkspaceSettings__member-row" style={{ marginBottom: 6 }}>
                        <i className="fas fa-link" style={{ color: "#5B92E5", fontSize: "0.8rem" }}></i>
                        <div className="WorkspaceSettings__member-info">
                            <div className="WorkspaceSettings__member-email">{link.label}</div>
                            <div className="WorkspaceSettings__member-meta">{link.url}</div>
                        </div>
                        <button className="WorkspaceSettings__member-remove-btn" onClick={() => removeQuickLink(link.id)} title="Remove">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <input
                        className="WorkspaceSettings__input"
                        type="text"
                        placeholder="Label (e.g. Style Guide)"
                        value={newLinkLabel}
                        onChange={e => setNewLinkLabel(e.target.value)}
                        style={{ flex: "1 1 140px" }}
                    />
                    <input
                        className="WorkspaceSettings__input"
                        type="text"
                        placeholder="URL"
                        value={newLinkUrl}
                        onChange={e => setNewLinkUrl(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addQuickLink()}
                        style={{ flex: "2 1 200px" }}
                    />
                    <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={addQuickLink}>
                        <i className="fas fa-plus"></i>
                    </button>
                </div>
            </div>

            {/* Checklist */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Onboarding Checklist <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                {obChecklist.map(item => (
                    <div key={item.id} className="WorkspaceSettings__widget-row" style={{ marginBottom: 6 }}>
                        <i className="fas fa-check-square" style={{ color: "#5B92E5", fontSize: "0.85rem" }}></i>
                        <span className="WorkspaceSettings__widget-label">{item.label}</span>
                        <button className="WorkspaceSettings__member-remove-btn" onClick={() => removeCheckItem(item.id)} title="Remove">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <input
                        className="WorkspaceSettings__input"
                        type="text"
                        placeholder="e.g. Read the contribution guidelines"
                        value={newCheckItem}
                        onChange={e => setNewCheckItem(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addCheckItem()}
                    />
                    <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={addCheckItem}>
                        <i className="fas fa-plus"></i>
                    </button>
                </div>
            </div>

            {/* Point of contact */}
            <div className="WorkspaceSettings__field-row">
                <label className="WorkspaceSettings__field-label">Point of Contact <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <select
                    className="WorkspaceSettings__theme-select"
                    value={obPocId}
                    onChange={e => setObPocId(e.target.value)}
                >
                    <option value="">None — no designated contact</option>
                    {teamMembers.map(m => (
                        <option key={m.userId} value={String(m.userId)}>
                            {m.name || m.email}{m.position ? ` — ${m.position}` : ""}
                        </option>
                    ))}
                </select>
                <p className="WorkspaceSettings__field-hint">
                    The person new members should reach out to with questions.
                </p>
            </div>

            <div className="WorkspaceSettings__btn-row">
                <button
                    className="WorkspaceSettings__btn WorkspaceSettings__btn--primary"
                    onClick={saveOnboarding}
                    disabled={isSavingOnboarding}
                >
                    {isSavingOnboarding
                        ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                        : <><i className="fas fa-save"></i> Save Onboarding</>
                    }
                </button>
                {onboardingSaved && (
                    <span style={{ fontSize: "0.8rem", color: "#16a34a", alignSelf: "center" }}>
                        <i className="fas fa-check-circle"></i> Saved
                    </span>
                )}
            </div>
        </>
    );

    const renderApiKeys = () => {
        const policy = wsApiKeyPolicy;
        const allowedScopes = policy?.your_policy?.allowed_scopes || ['read'];
        const maxRPH = policy?.your_policy?.max_rph || 100;
        const keysEnabled = policy?.api_keys_enabled ?? false;
        const yourRole = policy?.your_role || currentUserRole;
        const rolePolicies = policy?.role_policies || {};

        return (
            <>
                <div className="WorkspaceSettings__section-header">
                    <h2 className="WorkspaceSettings__section-title">API Keys</h2>
                    <p className="WorkspaceSettings__section-desc">
                        Generate workspace API keys to allow external tools and agents to read, create, and manage
                        documents in this workspace. Keys are shown only once — save them somewhere safe.
                    </p>
                </div>

                {/* Owner/Admin: Policy configuration panel */}
                {isAdminOrOwner && (
                    <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '28px' }}>
                        <h3 className="WorkspaceSettings__subsection-title" style={{ marginBottom: '4px' }}>API Key Policy</h3>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                            {currentUserRole === 'OWNER' ? 'Configure whether members can generate API keys and what permissions they may request.' : 'View the current API key policy for this workspace.'}
                        </p>

                        {/* Warning banner — shown when enabling */}
                        {currentUserRole === 'OWNER' && (
                            <div style={{ background: '#fff8e1', border: '1px solid #f9a825', borderRadius: '6px', padding: '14px', marginBottom: '18px', fontSize: '13px', lineHeight: '1.6' }}>
                                <p style={{ fontWeight: 600, marginBottom: '6px', color: '#5d4037' }}>
                                    <i className="fas fa-triangle-exclamation" style={{ marginRight: '8px' }}></i>
                                    Important — we strongly recommend restricting API key permissions by role.
                                </p>
                                <p style={{ margin: 0, color: '#5d4037' }}>
                                    Giving all roles broad scopes (e.g. write + delete) means any member with an API key can modify
                                    or remove workspace documents. At minimum, restrict <strong>Viewers</strong> to <strong>read-only</strong> and
                                    limit <strong>Editors</strong> to <strong>read + write</strong> only.
                                </p>
                            </div>
                        )}

                        <div className="WorkspaceSettings__field-row">
                            <label className="WorkspaceSettings__field-label">Enable API key generation</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: currentUserRole === 'OWNER' ? 'pointer' : 'default' }}>
                                <input
                                    type="checkbox"
                                    checked={keysEnabled}
                                    disabled={currentUserRole !== 'OWNER'}
                                    onChange={e => {
                                        const enabled = e.target.checked;
                                        setWsApiKeyPolicy(prev => ({ ...prev, api_keys_enabled: enabled }));
                                        if (currentUserRole === 'OWNER') {
                                            saveWsApiKeyPolicy({ api_keys_enabled: enabled });
                                        }
                                    }}
                                />
                                <span style={{ fontSize: '13px' }}>
                                    {keysEnabled ? 'Members can generate API keys' : 'API key generation is disabled'}
                                </span>
                            </label>
                        </div>

                        {currentUserRole === 'OWNER' && keysEnabled && (
                            <>
                                <p style={{ fontSize: '13px', fontWeight: 600, marginTop: '16px', marginBottom: '12px' }}>
                                    Per-role permissions
                                </p>
                                {['ADMIN', 'EDITOR', 'VIEWER'].map(role => {
                                    const rp = rolePolicies[role] || { allowed_scopes: ['read'], max_rph: 100 };
                                    return (
                                        <div key={role} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '14px', marginBottom: '12px' }}>
                                            <p style={{ fontWeight: 600, fontSize: '13px', marginBottom: '10px', textTransform: 'capitalize' }}>
                                                {role.charAt(0) + role.slice(1).toLowerCase()}
                                            </p>
                                            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                                {['read', 'write', 'delete'].map(scope => (
                                                    <label key={scope} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={(rp.allowed_scopes || []).includes(scope)}
                                                            onChange={e => {
                                                                const newScopes = e.target.checked
                                                                    ? [...(rp.allowed_scopes || []), scope]
                                                                    : (rp.allowed_scopes || []).filter(s => s !== scope);
                                                                const updated = {
                                                                    ...rolePolicies,
                                                                    [role]: { ...rp, allowed_scopes: newScopes },
                                                                };
                                                                setWsApiKeyPolicy(prev => ({ ...prev, role_policies: updated }));
                                                            }}
                                                        />
                                                        <span style={{ textTransform: 'capitalize' }}>{scope}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <label style={{ fontSize: '12px', color: '#666' }}>Max req/hr:</label>
                                                <select
                                                    className="WorkspaceSettings__theme-select"
                                                    style={{ width: 'auto' }}
                                                    value={rp.max_rph || 100}
                                                    onChange={e => {
                                                        const updated = {
                                                            ...rolePolicies,
                                                            [role]: { ...rp, max_rph: parseInt(e.target.value) },
                                                        };
                                                        setWsApiKeyPolicy(prev => ({ ...prev, role_policies: updated }));
                                                    }}
                                                >
                                                    {[25, 50, 100, 200, 500].map(v => (
                                                        <option key={v} value={v}>{v} req / hr</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="WorkspaceSettings__btn-row" style={{ marginTop: '8px' }}>
                                    <button
                                        className="WorkspaceSettings__btn WorkspaceSettings__btn--primary"
                                        onClick={() => saveWsApiKeyPolicy({ role_policies: rolePolicies })}
                                        disabled={wsIsSavingPolicy}
                                    >
                                        {wsIsSavingPolicy
                                            ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                                            : <><i className="fas fa-save"></i> Save Role Policies</>
                                        }
                                    </button>
                                    {wsPolicySaved && (
                                        <span style={{ fontSize: '0.8rem', color: '#16a34a', alignSelf: 'center' }}>
                                            <i className="fas fa-check-circle"></i> Saved
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Usage info box */}
                <div style={{ background: '#f0f4ff', border: '1px solid #c5d0f5', borderRadius: '8px', padding: '16px', marginBottom: '24px', fontSize: '13px', lineHeight: '1.7', color: '#1a1a1a' }}>
                    <p style={{ fontWeight: 600, marginBottom: '8px' }}>
                        <i className="fas fa-circle-info" style={{ marginRight: '8px', color: '#3b5bdb' }}></i>
                        How to use your workspace API key
                    </p>
                    <p style={{ marginBottom: '8px' }}>After generating a key, store it in a <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>.env</code> file in your project:</p>
                    <code style={{ display: 'block', background: '#d0d8f5', color: '#1a1a1a', padding: '8px 12px', borderRadius: '4px', fontFamily: 'monospace', marginBottom: '10px' }}>
                        BLUEBIRD_WORKSPACE_API_KEY=bbw_your_key_here
                    </code>
                    <p style={{ marginBottom: '8px' }}>Then pass the key as the <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>Authorization</code> header in requests:</p>
                    <code style={{ display: 'block', background: '#d0d8f5', color: '#1a1a1a', padding: '8px 12px', borderRadius: '4px', fontFamily: 'monospace', marginBottom: '10px' }}>
                        Authorization: bbw_your_key_here
                    </code>
                    <p style={{ marginBottom: '4px' }}>Key endpoints (base URL: <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>bluebirddocumentationadmin.pythonanywhere.com</code>):</p>
                    <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                        <li><code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>GET /workspaces/agent/{workspace?.id}/documents/</code> — list workspace documents</li>
                        <li><code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>POST /workspaces/agent/{workspace?.id}/documents/</code> — create a document</li>
                        <li><code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>GET /workspaces/agent/{workspace?.id}/documents/&#123;type&#125;/&#123;name&#125;/</code> — read a document</li>
                        <li><code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>PUT /workspaces/agent/{workspace?.id}/documents/&#123;type&#125;/&#123;name&#125;/</code> — update a document</li>
                        <li><code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>DELETE /workspaces/agent/{workspace?.id}/documents/&#123;type&#125;/&#123;name&#125;/</code> — delete a document</li>
                    </ul>
                    <p style={{ margin: 0 }}>Document content is sent and received as <strong>plain HTML</strong> (e.g. <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>&lt;h1&gt;</code>, <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>&lt;p&gt;</code>, <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>&lt;ul&gt;</code>). View the full spec at <code style={{ background: '#d0d8f5', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>/workspaces/agent/{workspace?.id}/spec/</code>.</p>
                </div>

                {/* Generated key display */}
                {wsGeneratedKey && (
                    <div style={{ background: '#fff8e1', border: '1px solid #f9a825', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                        <p style={{ fontWeight: 600, marginBottom: '10px', color: '#5d4037' }}>
                            <i className="fas fa-triangle-exclamation" style={{ marginRight: '8px' }}></i>
                            Save this key now — it will not be shown again.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <code style={{ flex: 1, background: '#f5f5f5', color: '#1a1a1a', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 12px', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                                {wsGeneratedKey}
                            </code>
                            <button
                                className="WorkspaceSettings__btn WorkspaceSettings__btn--secondary"
                                onClick={() => { navigator.clipboard.writeText(wsGeneratedKey); alert('Key copied to clipboard!'); }}
                            >
                                <i className="fas fa-copy" style={{ marginRight: '6px' }}></i>Copy
                            </button>
                        </div>
                        <button
                            className="WorkspaceSettings__btn WorkspaceSettings__btn--secondary"
                            style={{ marginTop: '12px' }}
                            onClick={() => setWsGeneratedKey(null)}
                        >
                            I've saved my key
                        </button>
                    </div>
                )}

                {wsApiKeysError && <div style={{ color: 'red', marginBottom: '12px', fontSize: '13px' }}>{wsApiKeysError}</div>}

                {/* Existing keys list */}
                {wsApiKeys.length > 0 && (
                    <div style={{ marginBottom: '28px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
                            {isAdminOrOwner ? 'All Workspace API Keys' : 'Your API Keys'}
                        </h3>
                        {wsApiKeys.map(key => (
                            <div key={key.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                    <div>
                                        <span style={{ fontWeight: 600 }}>{key.name}</span>
                                        <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>{key.key_prefix}…</span>
                                        {!key.is_active && (
                                            <span style={{ marginLeft: '8px', color: '#c0392b', fontSize: '12px' }}>
                                                {key.disabled_by_owner ? '(Disabled by owner)' : '(Revoked)'}
                                            </span>
                                        )}
                                        {isAdminOrOwner && key.member_email && (
                                            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#555' }}>— {key.member_email}</span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {key.is_active && (key.member_uid === userInfo?.uid || !key.member_uid) && (
                                            <button
                                                className="WorkspaceSettings__btn WorkspaceSettings__btn--danger-outline"
                                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                                onClick={() => revokeWsApiKey(key.id, key.name)}
                                            >
                                                Revoke
                                            </button>
                                        )}
                                        {key.is_active && currentUserRole === 'OWNER' && key.member_uid && key.member_uid !== userInfo?.uid && (
                                            <button
                                                className="WorkspaceSettings__btn WorkspaceSettings__btn--danger-outline"
                                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                                onClick={() => disableWsApiKey(key.id, key.name)}
                                            >
                                                Disable
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div style={{ marginTop: '6px', fontSize: '12px', color: '#666', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    <span>Scopes: <strong>{key.scopes.join(', ')}</strong></span>
                                    <span>{key.requests_per_hour} req/hr</span>
                                    {key.last_used_at && <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>}
                                    {key.expires_at && <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Create new key form — only if policy allows */}
                {(keysEnabled || currentUserRole === 'OWNER') ? (
                    <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Create New API Key</h3>

                        <div className="WorkspaceSettings__field-row">
                            <label className="WorkspaceSettings__field-label">Key Name</label>
                            <input
                                className="WorkspaceSettings__input"
                                type="text"
                                placeholder="e.g. Claude Code, Automation Script"
                                value={wsNewKeyName}
                                onChange={e => setWsNewKeyName(e.target.value)}
                            />
                        </div>

                        <div className="WorkspaceSettings__field-row">
                            <label className="WorkspaceSettings__field-label">Permissions</label>
                            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                {['read', 'write', 'delete'].map(scope => (
                                    <label key={scope} style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: allowedScopes.includes(scope) ? 'pointer' : 'not-allowed', opacity: allowedScopes.includes(scope) ? 1 : 0.4 }}>
                                        <input
                                            type="checkbox"
                                            checked={wsNewKeyScopes[scope]}
                                            disabled={!allowedScopes.includes(scope)}
                                            onChange={e => setWsNewKeyScopes(prev => ({ ...prev, [scope]: e.target.checked }))}
                                        />
                                        <span style={{ textTransform: 'capitalize' }}>{scope}</span>
                                    </label>
                                ))}
                            </div>
                            {yourRole && yourRole !== 'OWNER' && (
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                                    Your role ({yourRole}) allows: {allowedScopes.join(', ')}
                                </p>
                            )}
                        </div>

                        <div className="WorkspaceSettings__field-row">
                            <label className="WorkspaceSettings__field-label">Rate Limit</label>
                            <select
                                className="WorkspaceSettings__theme-select"
                                value={wsNewKeyRPH}
                                onChange={e => setWsNewKeyRPH(parseInt(e.target.value))}
                            >
                                {[25, 50, 100, 200, 500].filter(v => v <= maxRPH).map(v => (
                                    <option key={v} value={v}>{v} requests / hour</option>
                                ))}
                            </select>
                        </div>

                        <button
                            className="WorkspaceSettings__btn WorkspaceSettings__btn--primary"
                            onClick={createWsApiKey}
                            disabled={wsApiKeysLoading}
                            style={{ marginTop: '4px' }}
                        >
                            {wsApiKeysLoading
                                ? <><i className="fas fa-spinner fa-spin"></i> Generating…</>
                                : <><i className="fas fa-key"></i> Generate Key</>
                            }
                        </button>
                    </div>
                ) : (
                    <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', fontSize: '13px', color: '#666' }}>
                        <i className="fas fa-lock" style={{ marginRight: '8px' }}></i>
                        API key generation is currently disabled for this workspace. Contact the workspace owner to enable it.
                    </div>
                )}
            </>
        );
    };

    const renderTemplates = () => {
        const canCreate = ["OWNER", "ADMIN", "EDITOR"].includes(currentUserRole);
        const kindLabel = { page: "Page", space: "Space", kit: "Kit", snippet: "Snippet" };
        return (
            <>
                <div className="WorkspaceSettings__section-header">
                    <h2 className="WorkspaceSettings__section-title">Templates</h2>
                    <p className="WorkspaceSettings__section-desc">
                        Templates shared with everyone in this workspace. Featured templates appear first in the
                        gallery; the default template is applied whenever a member creates a new document.
                    </p>
                </div>

                {wsTemplatesError && <div className="TplGallery__error" style={{ padding: "0 0 10px" }}>{wsTemplatesError}</div>}

                {isAdminOrOwner && (
                    <div className="WorkspaceSettings__field-row">
                        <label className="WorkspaceSettings__field-label">Default template for new documents</label>
                        <select
                            className="WorkspaceSettings__theme-select"
                            style={{ maxWidth: 420 }}
                            value={wsDefaultTemplateRef}
                            onChange={(e) => saveDefaultTemplateRef(e.target.value)}
                        >
                            <option value="">Start blank</option>
                            {wsTemplates.filter(t => t.kind !== "snippet").length > 0 && (
                                <optgroup label="This workspace">
                                    {wsTemplates.filter(t => t.kind !== "snippet").map(t => (
                                        <option key={t.ref} value={t.ref}>{t.name}</option>
                                    ))}
                                </optgroup>
                            )}
                            <optgroup label="Bluebird presets">
                                {wsPresets.map(p => (
                                    <option key={p.ref} value={p.ref}>{p.name}</option>
                                ))}
                            </optgroup>
                        </select>
                        <div className="WorkspaceSettings__field-hint">"Start blank" is always one click away on the create page.</div>
                    </div>
                )}

                <div className="WorkspaceSettings__btn-row" style={{ marginBottom: 12 }}>
                    {canCreate && (
                        <button className="WorkspaceSettings__btn WorkspaceSettings__btn--primary" onClick={() => setTemplateEditor({ open: true, template: null })}>
                            <i className="fas fa-plus"></i> New workspace template
                        </button>
                    )}
                    <button className="WorkspaceSettings__btn WorkspaceSettings__btn--ghost" onClick={() => { setWsTemplatesLoaded(false); fetchWsTemplates(); }}>
                        <i className="fas fa-sync"></i> Refresh
                    </button>
                </div>

                {wsTemplates.length === 0 ? (
                    <div className="WorkspaceSettings__empty">
                        No workspace templates yet. Open any workspace document and choose <strong>Save as template</strong>, or create one here.
                    </div>
                ) : wsTemplates.map(t => (
                    <div key={t.id} className="WorkspaceSettings__member-row">
                        <div className="WorkspaceSettings__member-avatar"><i className={`fas ${{ space: "fa-folder", kit: "fa-layer-group", snippet: "fa-puzzle-piece" }[t.kind] || "fa-file-lines"}`} aria-hidden="true"></i></div>
                        <div className="WorkspaceSettings__member-info">
                            <div className="WorkspaceSettings__member-email">
                                {t.name}
                                <span className="WorkspaceSettings__badge" style={{ marginLeft: 8 }}>{kindLabel[t.kind] || t.kind}</span>
                                {t.is_featured && <span className="WorkspaceSettings__badge WorkspaceSettings__badge--new-user" style={{ marginLeft: 6 }}>Featured</span>}
                            </div>
                            <div className="WorkspaceSettings__member-meta">
                                {t.description ? `${t.description} · ` : ""}by {t.owner_name || t.owner_uid} · used {t.usage_count} {t.usage_count === 1 ? "time" : "times"}
                            </div>
                        </div>
                        <div className="WorkspaceSettings__btn-row" style={{ flexWrap: "wrap", gap: 6 }}>
                            {t.permissions?.can_feature && (
                                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--ghost" disabled={templateBusyId === t.id}
                                    onClick={() => withTemplateBusy(t.id, async () => { await templatesApi.update(t.id, { is_featured: !t.is_featured }); await fetchWsTemplates(); })}>
                                    {t.is_featured ? "Unfeature" : "Feature"}
                                </button>
                            )}
                            {t.permissions?.can_edit && (
                                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--ghost" disabled={templateBusyId === t.id}
                                    onClick={() => withTemplateBusy(t.id, async () => { const full = await templatesApi.get(t.id); setTemplateEditor({ open: true, template: full }); })}>
                                    Edit
                                </button>
                            )}
                            <button className="WorkspaceSettings__btn WorkspaceSettings__btn--ghost" disabled={templateBusyId === t.id}
                                onClick={() => withTemplateBusy(t.id, async () => { const copy = await templatesApi.copy(t.id, { workspaceId: null }); alert(`Copied as "${copy.name}" to My Templates.`); })}>
                                Copy to my templates
                            </button>
                            {t.permissions?.can_edit && (
                                <button className="WorkspaceSettings__btn WorkspaceSettings__btn--danger-outline" disabled={templateBusyId === t.id}
                                    onClick={() => { if (window.confirm(`Delete the template "${t.name}"?`)) withTemplateBusy(t.id, async () => { await templatesApi.remove(t.id); await fetchWsTemplates(); }); }}>
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}

                <TemplateEditorModal
                    isOpen={templateEditor.open}
                    template={templateEditor.template}
                    workspaceId={workspace?.id}
                    onClose={() => setTemplateEditor({ open: false, template: null })}
                    onSaved={() => fetchWsTemplates()}
                />
            </>
        );
    };

    const sectionContent = {
        profile:    renderProfile,
        templates:  renderTemplates,
        dashboard:  renderDashboard,
        general:    renderGeneral,
        domain:     renderDomain,
        members:    renderMembers,
        boards:     renderBoards,
        theme:      renderTheme,
        onboarding: renderOnboarding,
        apikeys:    renderApiKeys,
        danger:     renderDanger,
    };

    // Ensure activeSection is valid for this user's role
    const validSection = visibleSections.find(s => s.id === activeSection)
        ? activeSection
        : visibleSections[0]?.id;

    return (
        <div
            className="WorkspaceSettings__shell"
            onContextMenu={enhancedRightClickEnabled ? (e) => { e.preventDefault(); showContextMenu(e.clientX, e.clientY); } : undefined}
        >
            {/* ── Sidebar ── */}
            <aside className="WorkspaceSettings__sidebar">
                <div className="WorkspaceSettings__sidebar-header">
                    <p className="WorkspaceSettings__sidebar-title">Settings</p>
                    <p className="WorkspaceSettings__sidebar-workspace">{workspace?.name}</p>
                </div>

                <nav className="WorkspaceSettings__nav">
                    <div className="WorkspaceSettings__nav-group">
                        <p className="WorkspaceSettings__nav-group-label">Personal</p>
                        {visibleSections.filter(s => s.always).map(s => (
                            <button
                                key={s.id}
                                className={`WorkspaceSettings__nav-item${validSection === s.id ? " WorkspaceSettings__nav-item--active" : ""}`}
                                onClick={() => setActiveSection(s.id)}
                            >
                                <i className={s.icon}></i> {s.label}
                            </button>
                        ))}
                    </div>

                    {isAdminOrOwner && (
                        <div className="WorkspaceSettings__nav-group">
                            <p className="WorkspaceSettings__nav-group-label">Workspace</p>
                            {visibleSections.filter(s => !s.always && s.id !== "danger").map(s => (
                                <button
                                    key={s.id}
                                    className={`WorkspaceSettings__nav-item${validSection === s.id ? " WorkspaceSettings__nav-item--active" : ""}`}
                                    onClick={() => setActiveSection(s.id)}
                                >
                                    <i className={s.icon}></i> {s.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {currentUserRole === "OWNER" && (
                        <div className="WorkspaceSettings__nav-group" style={{ marginTop: "auto" }}>
                            <button
                                className={`WorkspaceSettings__nav-item WorkspaceSettings__nav-item--danger${validSection === "danger" ? " WorkspaceSettings__nav-item--active" : ""}`}
                                onClick={() => setActiveSection("danger")}
                            >
                                <i className="fas fa-exclamation-triangle"></i> Danger Zone
                            </button>
                        </div>
                    )}
                </nav>

                <div className="WorkspaceSettings__sidebar-footer">
                    <button className="WorkspaceSettings__back-link" onClick={() => navigate(`/workspace/${workspace?.custom_domain}`)}>
                        <i className="fas fa-arrow-left"></i> Back to workspace
                    </button>
                </div>
            </aside>

            {/* ── Content ── */}
            <main className="WorkspaceSettings__main">
                {sectionContent[validSection] && sectionContent[validSection]()}
            </main>

            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default WorkspaceSettings;
